<template>
	<div
		class="media-preview-container"
		:class="{ animate, expanded: count > 0, small, 'only-files': onlyFiles }"
		@transitionend="onTransitionEnd"
	>
		<DragSort
			v-if="count > 0"
			:items="itemList"
			:itemKey="itemKey"
			class="media-preview-items"
			:class="{ 'has-multiple': count > 1 }"
			@change="emit('reorder', $event)"
		>
			<template #item="{ item }">
				<div
					class="media-item ph-no-capture"
					:key="item.mediaUrl"
					:class="{ visual: item.isVisual }"
					:style="item.style"
					:title="`${item.title || ''} (${item.size})`"
					@mousedown="onMouseDown"
					@mouseup="onMouseUp($event, item)"
				>
					<div class="video-overlay" v-if="item.isVideo">
						<PlayIcon type="solid" />
					</div>
					<MediaFile v-if="item.isFile" :media="item" />
					<AudioPlayer
						v-if="item.isAudio"
						:src="item.mediaUrl"
						:duration="item.duration"
					/>
					<div
						v-if="!hideRemove"
						class="media-preview-remove"
						@click.stop="removeItem(item)"
					>
						<!-- TODO: replace this with the Removeable component -->
						<CloseIcon type="solid" />
					</div>
					<ProgressPie
						v-if="item.progress"
						:size="16"
						:percent="item.progress"
					/>
				</div>
			</template>
		</DragSort>
	</div>
</template>

<script setup>
import { computed } from 'vue'
import { CloseIcon, PlayIcon } from '@/icons'
import MediaFile from '@/components/MediaView/MediaFile'
import AudioPlayer from '@/components/MediaView/AudioPlayer'
import { ProgressPie, DragSort } from '@/components/common'
import { getUpload } from '@/store'

const props = defineProps({
	mediaItems: Array,
	itemKey: { type: String, default: 'tempId' },
	small: { type: Boolean, default: false },
	animate: { type: Boolean, default: false },
	hideRemove: { type: Boolean, default: false },
})
const emit = defineEmits(['remove', 'reorder', 'click'])

const mapItems = (items) => {
	return items?.map((m) => {
		const upld = getUpload(m.tempId) || {}
		const progress =
			upld.progress > 0 && upld.progress < 100 ? upld.progress : 0
		const styleUrl = m.isVisual
			? m.isVideo
				? m.thumbnailUrl
				: m.mediaUrl
			: null
		const style = styleUrl ? { 'background-image': `url(${styleUrl})` } : null
		return { ...m, progress, style }
	})
}

let mouseX = 0,
	mouseY = 0,
	mouseDelta = 5
const itemList = computed(() => mapItems(props.mediaItems))
const count = computed(() => itemList.value?.length || 0)
const onlyFiles = computed(
	() => itemList.value?.every((m) => m.isFile) || false
)

const removeItem = (media) => {
	emit('remove', media)
}
const onMouseDown = (e) => {
	mouseX = e.pageX
	mouseY = e.pageY
}
const onMouseUp = (e, media) => {
	const isRemoveAction = e.target.closest('.media-preview-remove')
	if (isRemoveAction) return
	// since items are draggable, it is hard to differentiate between a 'click' action
	// and the start of a drag action. So this uses two events to figure out which is happening
	// see: https://stackoverflow.com/questions/6042202/how-to-distinguish-mouse-click-and-drag
	const diffX = Math.abs(e.pageX - mouseX)
	const diffY = Math.abs(e.pageY - mouseY)
	if (diffX < mouseDelta && diffY < mouseDelta) {
		emit('click', media, itemList.value)
	}
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import 'src/sass/variables';

.media-preview-container {
	$item-size: 90px;
	$item-size-sm: 62px;
	$item-size-file: 57px;
	$remove-size: 24px;

	&.animate {
		height: 0;
		overflow: hidden;
		transition: height ease-in-out 0.3s;
		&.expanded {
			height: $item-size + $remove-size;
			&.small {
				height: $item-size-sm + $remove-size;
			}
			&.only-files {
				height: $item-size-file + $remove-size;
			}
		}
	}

	&.small {
		:deep(.media-preview-items) {
			.media-item {
				&.visual {
					width: $item-size-sm;
					height: $item-size-sm;
				}
			}
		}
	}

	:deep(.media-preview-items) {
		@include flex(24px);
		padding-top: math.div($remove-size, 2);
		max-width: 100%;
		overflow-x: auto;
		overflow-y: hidden;
		&.has-multiple {
			.media-item {
				cursor: move;
			}
		}
		.media-item {
			cursor: pointer;
			position: relative;
			flex-shrink: 0;
			&.visual {
				width: $item-size;
				height: $item-size;
				@include radius;
				background-size: cover;
				background-position: center;
				background-repeat: no-repeat;
			}
			&.ghost {
				opacity: 0.4;
				.remove {
					display: none;
				}
			}
			&.dragger {
				.remove {
					display: none;
				}
				.media-file-container {
					background-color: var(--neutral-0);
				}
			}
			.video-overlay {
				@include absolute-cover(1);
				@include flex;
				justify-content: center;
				background-color: rgba(0, 0, 0, 0.4);
				border-radius: 6px;
				.pronto-icon {
					font-size: 32px;
					color: white;
				}
			}
			.media-preview-remove {
				$half: math.div($remove-size, 2);
				width: $remove-size;
				height: $remove-size;
				border-radius: $remove-size;
				background: var(--neutral-600);
				color: var(--neutral-100);
				border: 2px solid var(--neutral-100);
				position: absolute;
				top: -#{$half};
				right: -#{$half};
				z-index: 2;
				@include flex;
				justify-content: center;
				cursor: pointer;
				&:hover {
					background: var(--neutral-700);
				}
				.pronto-icon {
					font-size: $half;
				}
			}
			.pronto-progress-pie {
				position: absolute;
				top: 8px;
				left: 8px;
				z-index: 3;
			}
			.media-file-container {
				max-width: 100%;
			}
		}
	}
}
</style>
